import { useKeenSlider } from "keen-slider/react";
import { useEffect, useState } from "react";
import {
  AppAvatar,
  AppBorderBox,
  AppBox,
  AppButton,
  AppIconButton,
  AppPaper,
  AppSkeletonAwardList,
  AppSkeletonStatistics,
  AppText,
  AppTitle,
} from "../../../../commons/components";
import IconChevronLeft from "../../../../commons/components/icons/chevron-left";
import IconChevronRight from "../../../../commons/components/icons/chevron-right";
import { useAppDispatch, useAppSelector } from "../../../../hooks/app";
import { fetchManageTeamList } from "../../../../redux/slices/football/admin/team/manage-team-list-slice";
import { StatusEnum } from "../../../../commons/enums/status-enum";
import TeamStatisticsCard from "../../../public/team-detail/components/TeamStatisticsCard";
import { ManageFootballTeam } from "../../../../commons/models/football/admin/manage-football-team";
import { fetchManageTeamBasicStatistics } from "../../../../redux/slices/football/admin/team/manage-team-basic-statistics-slice";
import { useNavigate } from "react-router";
import IconSquareCornerArrow from "../../../../commons/components/icons/square-corner-arrow";
import { Tooltip } from "react-tooltip";

const TeamStatisticsList = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { teamList, teamListStatus } = useAppSelector((state) => state.footballManageTeamList);
  const { teamStatistics, teamStatisticsStatus } = useAppSelector((state) => state.footballManageTeamStatistics);
  const { theme } = useAppSelector((state) => state.session);
  const [selectedTeam, setSelectedTeam] = useState<ManageFootballTeam | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    loop: true,
    drag: false,
    slideChanged(slider) {
      const currentIndex = slider.track.details.rel;
      const team = teamList.getItem(currentIndex);
      setSelectedTeam(team);
    },
    created(slider) {
      setLoaded(true);
      const initialIndex = slider.track.details.rel;
      setSelectedTeam(teamList.getItem(initialIndex));
    },
  });

  useEffect(() => {
    dispatch(fetchManageTeamList({}));
  }, []);

  useEffect(() => {
    if (teamList.length > 0) {
      setSelectedTeam(teamList.getItem(0));
    }
  }, [teamList]);

  useEffect(() => {
    if (selectedTeam && selectedTeam.id) {
      dispatch(
        fetchManageTeamBasicStatistics({
          teamId: selectedTeam.id,
        })
      );
    }
  }, [selectedTeam]);

  const handleNextClick = (e: any) => {
    e.stopPropagation();
    instanceRef.current?.next();
    const nextIndex = (instanceRef.current?.track.details.rel ?? 0) + 1;
    setSelectedTeam(teamList.getItem(nextIndex % teamList.length));
  };

  const handlePrevClick = (e: any) => {
    e.stopPropagation();
    instanceRef.current?.prev();
    const prevIndex = (instanceRef.current?.track.details.rel ?? 0) - 1 + teamList.length;
    setSelectedTeam(teamList.getItem(prevIndex % teamList.length));
  };

  const handleStatsBtnClick = (e: any) => {
    e.stopPropagation();
    if (selectedTeam?.id) {
      navigate(`/manage/teams/${selectedTeam.id}/statistics`);
    }
  };

  return (
    <AppBox flexDirection="column" gap="xs" className="w-100">
      <AppTitle as="h5">Teams</AppTitle>
      {teamListStatus === StatusEnum.Loading ? (
        <AppSkeletonAwardList numberOfItems={1} />
      ) : (
        <AppPaper padding="xs" shadow="xs">
          {teamList.length > 0 ? (
            <AppBox className="w-100" flexDirection="column" gap="xs">
              <AppBox gap="xs" alignItems="center">
                {teamList.length > 1 && (
                  <AppIconButton
                    size="xs"
                    icon={<IconChevronLeft />}
                    color="dark"
                    variant="subtle"
                    onClick={handlePrevClick}
                    disabled={!loaded || teamStatisticsStatus === StatusEnum.Loading}
                  />
                )}
                <AppBox ref={sliderRef} className="keen-slider">
                  {teamList.mapArray((team, index) => (
                    <AppBorderBox radius="sm" className="keen-slider__slide" py="2xs" px="xs" key={index}>
                      <AppBox gap="xs" alignItems="center" justifyContent="center">
                        <AppAvatar size="sm" username={team.name} src={team.logo || ""} />
                        <AppTitle as="h6">{team.shortName}</AppTitle>
                      </AppBox>
                    </AppBorderBox>
                  ))}
                </AppBox>
                {teamList.length > 1 && (
                  <AppIconButton
                    size="xs"
                    icon={<IconChevronRight />}
                    color="gray"
                    variant="subtle"
                    onClick={handleNextClick}
                    disabled={!loaded || teamStatisticsStatus === StatusEnum.Loading}
                  />
                )}
              </AppBox>
              <AppBorderBox p="xs" radius="sm">
                {teamStatisticsStatus === StatusEnum.Loading ? (
                  <AppBox style={{ height: "22.625rem" }}>
                    <AppSkeletonStatistics noTitle withoutPadding />
                  </AppBox>
                ) : (
                  <AppBox flexDirection="column" alignItems="center" gap="xs" style={{ position: "relative" }}>
                    <TeamStatisticsCard teamStats={teamStatistics} withoutPadding withoutShadow />
                    <span style={{ position: "absolute", right: "0" }} data-tooltip-id="team-stats-btn">
                      <AppIconButton icon={<IconSquareCornerArrow />} variant="subtle" onClick={handleStatsBtnClick} />
                    </span>
                    <Tooltip
                      id="team-stats-btn"
                      content={"View Detail Statistics"}
                      place="left"
                      variant={theme === "dark" ? "light" : "dark"}
                    />
                  </AppBox>
                )}
              </AppBorderBox>
            </AppBox>
          ) : (
            <AppBox
              flexDirection="column"
              gap="xs"
              alignItems="center"
              justifyContent="center"
              style={{ height: "15rem" }}
            >
              <AppText color="muted">There are not any teams.</AppText>
            </AppBox>
          )}
        </AppPaper>
      )}
    </AppBox>
  );
};

export default TeamStatisticsList;
